'use client';

import { useState } from 'react';
import { Download, Share, SquarePlus, Smartphone } from 'lucide-react';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * Early, low-height discovery surface for the PWA install -- placed right
 * after the hero so mobile visitors encounter it within the first couple of
 * scroll-heights instead of only at the footer (PwaInstallSection, which
 * stays lower on the page as a secondary fallback). Inline content, never a
 * popup: it does not interrupt reading and never opens itself.
 *
 * Visibility is about install SUPPORT, not about whether the native
 * beforeinstallprompt event has fired yet -- that Chromium-only signal is
 * often unavailable on a first visit (engagement heuristics, timing) and
 * gating on it would hide install education from most real Android/Chrome
 * visitors. Hidden only once actually running standalone, or on an iOS
 * browser that isn't Safari (the only iOS browser Apple allows to install a
 * PWA at all). Every other case shows this section: with the native-prompt
 * button when available, iOS "Add to Home Screen" steps on iOS Safari, or a
 * generic browser-menu fallback tip everywhere else.
 */
export default function PwaHomeScreenSection() {
  const { isStandalone, canInstall, isIOS, isIOSSafari, promptInstall } = usePwaInstall();
  const { t } = useTranslation();
  const [showGuidance, setShowGuidance] = useState(false);

  if (isStandalone) return null;
  if (isIOS && !isIOSSafari) return null;

  return (
    <section className="landing-install-band relative overflow-hidden py-5">
      <div className="mx-auto flex max-w-screen-xl flex-col items-center gap-3 px-6 text-center sm:flex-row sm:text-left lg:px-10">
        <div className="landing-install-icon flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl">
          <Smartphone size={18} aria-hidden="true" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-[var(--public-text)]">
            {t('pwa.discover_title' as any)}
          </p>
          <p className="text-xs leading-relaxed text-[var(--public-text-muted)]">
            {t('pwa.discover_description' as any)}
          </p>
        </div>

        <div className="flex flex-shrink-0 flex-col items-center gap-2 sm:items-end">
          <button
            type="button"
            onClick={() => {
              if (canInstall) void promptInstall();
              else setShowGuidance((open) => !open);
            }}
            aria-expanded={!canInstall ? showGuidance : undefined}
            className="landing-install-button inline-flex items-center gap-2 rounded-[10px] px-4 py-2 text-xs font-bold"
          >
            <Download size={13} aria-hidden="true" />
            {t('pwa.install_action' as any)}
          </button>

          {!canInstall && showGuidance && (
            <div
              role="dialog"
              aria-label={t('pwa.install_action' as any)}
              className="landing-install-dialog w-56 rounded-xl border p-3 text-left"
            >
              {isIOSSafari ? (
                <ol className="flex flex-col gap-1.5 text-xs font-medium text-[var(--public-text)]">
                  <li className="flex items-center gap-1.5">
                    <Share size={12} className="text-[var(--public-teal)]" aria-hidden="true" />
                    {t('pwa.ios_step_share' as any)}
                  </li>
                  <li className="flex items-center gap-1.5">
                    <SquarePlus
                      size={12}
                      className="text-[var(--public-teal)]"
                      aria-hidden="true"
                    />
                    {t('pwa.ios_step_add_home' as any)}
                  </li>
                  <li className="flex items-center gap-1.5">
                    <Download size={12} className="text-[var(--public-teal)]" aria-hidden="true" />
                    {t('pwa.ios_step_add' as any)}
                  </li>
                </ol>
              ) : (
                <p className="text-xs font-medium leading-relaxed text-[var(--public-text)]">
                  {t('pwa.generic_install_tip' as any)}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
